import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/images/logo.png';
import ThemeToggle from './ThemeToggle';

const NAV_ITEMS = [
  { to: '/', label: 'Startseite' },
  { to: '/ueber-uns', label: 'Über mich' },
  { to: '/leistungen', label: 'Leistungen' },
  { to: '/galerie', label: 'Galerie' },
  { to: '/kontakt', label: 'Kontakt' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => document.body.classList.remove('menu-open');
  }, [isMobileMenuOpen]);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={[
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          'bg-white/70 dark:bg-wood-950/70 backdrop-blur-md',
          scrolled ? 'shadow-soft' : '',
        ].join(' ')}
        aria-label="Hauptnavigation"
      >
        <div className="container flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            aria-label="Zur Startseite"
          >
            <span className="relative">
              <img
                src={logo}
                alt="SS-craftedwithlove Logo"
                className="h-10 w-10 md:h-12 md:w-12 object-contain transition-transform duration-500 group-hover:rotate-6"
              />
              <span className="absolute inset-0 rounded-full bg-gold-300/30 opacity-0 group-hover:opacity-100 blur-xl transition-opacity" aria-hidden="true" />
            </span>
            <span className="font-display text-lg md:text-xl font-semibold tracking-tight text-wood-800 dark:text-cream-50 transition-colors">
              craftedwithlove
            </span>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const active = location.pathname === item.to;
              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={[
                      'relative px-4 py-2 rounded-full text-sm font-medium transition-colors',
                      active
                        ? 'text-wood-900 dark:text-cream-50'
                        : 'text-wood-700 dark:text-cream-100/80 hover:text-wood-900 dark:hover:text-cream-50',
                    ].join(' ')}
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full bg-wood-100 dark:bg-wood-800"
                        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                        aria-hidden="true"
                      />
                    )}
                    <span className="relative z-10">{item.label}</span>
                  </Link>
                </li>
              );
            })}
            <li className="ml-2 flex items-center gap-2">
              <ThemeToggle />
              <Link
                to="/kontakt"
                className="btn btn-primary !py-2 !px-5 !text-sm"
              >
                Anfrage
              </Link>
            </li>
          </ul>

          {/* Mobile actions */}
          <div className="lg:hidden flex items-center gap-2">
            <ThemeToggle />
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              aria-expanded={isMobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
              className="relative w-10 h-10 flex items-center justify-center rounded-full
                         bg-white/70 dark:bg-wood-800/70 backdrop-blur-sm
                         ring-1 ring-wood-200 dark:ring-wood-700 shadow-soft
                         hover:bg-white dark:hover:bg-wood-700 transition-colors"
            >
              <span className="sr-only">Menü</span>
              <span className="relative w-5 h-4 block">
                <span
                  className={[
                    'absolute left-0 right-0 top-0 h-0.5 bg-wood-700 dark:bg-cream-100 rounded-full transition-all duration-300',
                    isMobileMenuOpen ? 'translate-y-1.5 rotate-45' : '',
                  ].join(' ')}
                />
                <span
                  className={[
                    'absolute left-0 right-0 top-1.5 h-0.5 bg-wood-700 dark:bg-cream-100 rounded-full transition-all duration-300',
                    isMobileMenuOpen ? 'opacity-0' : 'opacity-100',
                  ].join(' ')}
                />
                <span
                  className={[
                    'absolute left-0 right-0 top-3 h-0.5 bg-wood-700 dark:bg-cream-100 rounded-full transition-all duration-300',
                    isMobileMenuOpen ? '-translate-y-1.5 -rotate-45' : '',
                  ].join(' ')}
                />
              </span>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile drawer */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-40 bg-wood-900/40 backdrop-blur-sm lg:hidden"
              onClick={closeMobileMenu}
              aria-hidden="true"
            />
            <motion.div
              id="mobile-menu"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 32 }}
              className="fixed top-0 right-0 bottom-0 z-50 w-[85%] max-w-sm bg-cream-50 dark:bg-wood-900
                         shadow-2xl lg:hidden flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label="Mobile Navigation"
            >
              <div className="flex items-center justify-between h-16 px-6 border-b border-wood-100 dark:border-wood-800">
                <span className="font-display text-lg font-semibold text-wood-800 dark:text-cream-50">
                  craftedwithlove
                </span>
                <button
                  type="button"
                  onClick={closeMobileMenu}
                  className="w-9 h-9 rounded-full bg-wood-100 dark:bg-wood-800 hover:bg-wood-200 dark:hover:bg-wood-700 text-wood-700 dark:text-cream-100 flex items-center justify-center transition-colors"
                  aria-label="Menü schließen"
                >
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path d="M5 5L15 15M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto p-6">
                <ul className="space-y-1">
                  {NAV_ITEMS.map((item, i) => {
                    const active = location.pathname === item.to;
                    return (
                      <motion.li
                        key={item.to}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.05 + i * 0.05 }}
                      >
                        <Link
                          to={item.to}
                          onClick={closeMobileMenu}
                          className={[
                            'block px-4 py-3 rounded-xl text-base font-medium transition-colors',
                            active
                              ? 'bg-wood-100 dark:bg-wood-800 text-wood-900 dark:text-cream-50'
                              : 'text-wood-700 dark:text-cream-100/80 hover:bg-wood-50 dark:hover:bg-wood-800/60',
                          ].join(' ')}
                        >
                          {item.label}
                        </Link>
                      </motion.li>
                    );
                  })}
                </ul>

                <div className="mt-8 pt-6 border-t border-wood-100 dark:border-wood-800">
                  <Link
                    to="/kontakt"
                    onClick={closeMobileMenu}
                    className="btn btn-primary w-full"
                  >
                    Anfrage stellen
                  </Link>
                </div>
              </nav>

              <div className="p-6 border-t border-wood-100 dark:border-wood-800 text-sm text-wood-600 dark:text-cream-100/70">
                <p className="font-medium text-wood-800 dark:text-cream-50 mb-1">SS-craftedwithlove</p>
                <p>Am Brunnenfeld 20, 86653 Daiting</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

    </>
  );
};

export default Navbar;
